import React, { useState, useEffect, useCallback } from "react";
import PaymentWrapper from "./PaymentWrapper";

const AppointmentBooking = ({
  onAppointmentComplete,
  customerInfo = {},
  sessionId = null,
  formData = {},
  usePayment = false,
  hideButtons = false,
  onBookingReady = null,
  onInputChange = null,
}) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";
  // State management
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedEndTime, setSelectedEndTime] = useState("");
  const [timezone, setTimezone] = useState("");
  const [timeSlots, setTimeSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [serviceInfo, setServiceInfo] = useState({
    name: "Loading...",
    provider: "Loading...",
    duration: "-",
    price: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [showPayment, setShowPayment] = useState(false);

  // Form data state
  const [appointmentFormData, setAppointmentFormData] = useState({
    subject: "Sprinter Van Rental with Driver",
    description: "",
    firstname: customerInfo.first_name || "",
    lastname: customerInfo.last_name || "",
    email: customerInfo.email || "",
    phone: "",
    address: "",
  });

  // Configuration
  const CONFIG = {
    API_BASE: "https://crmv2.nashvilletnconcierge.com",
    SERVICE_IDS: [4, 5], // Multiple services to choose from
    PROVIDER_ID: 1,
  };



  // Helper function to convert minutes to hours display
  const formatDuration = (minutes) => {
    const mins = parseInt(minutes);
    if (isNaN(mins)) return minutes;
    
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    
    if (hours === 0) {
      return `${mins} min`;
    } else if (remainingMins === 0) {
      return `${hours} hr`;
    } else {
      return `${hours} hr ${remainingMins} min`;
    }
  };

  // Helper function to convert 24h time to 12h format
  const convertTo12Hour = (time24) => {
    if (!time24) return "";
    // Check if time is in HH:MM format
    if (!time24.includes(":")) return time24;
    
    const [hours, minutes] = time24.split(":");
    let h = parseInt(hours, 10);
    const suffix = h >= 12 ? "PM" : "AM";
    
    h = h % 12;
    h = h ? h : 12; // 0 should be 12
    
    return `${h}:${minutes} ${suffix}`;
  };

  // Initialize component
  useEffect(() => {
    initializeComponent();
  }, []);

  // Handle payment requirement
  useEffect(() => {
    if (!usePayment) {
      setPaymentComplete(true);
    } else {
      setPaymentComplete(false);
    }
  }, [usePayment]);

  // Notify parent when booking is ready
  useEffect(() => {
    // Add validation for location fields - Check if they exist in formData and are not empty
    const hasLocationInfo = formData.pickup_location && 
                           formData.pickup_location.trim() !== "" && 
                           formData.dropoff_location && 
                           formData.dropoff_location.trim() !== "";

    const isBookingReady = selectedDate && selectedTime && (!usePayment || paymentComplete) && !isBooked && !isSubmitting && hasLocationInfo;
    if (onBookingReady) {
      onBookingReady(isBookingReady, handleSubmit);
    }
  }, [selectedDate, selectedTime, paymentComplete, usePayment, isBooked, isSubmitting, onBookingReady, formData.pickup_location, formData.dropoff_location]);

  const initializeComponent = async () => {
    // Set timezone to Central Time (Nashville is in Central Time Zone)
    setTimezone("America/Chicago");

    // Load service info
    await loadServiceInfo();
  };

  const loadServiceInfo = async () => {
    try {
      // Load service information
      const serviceResponse = await fetch(
        `${CONFIG.API_BASE}/appointly/appointments_public/get_services`,
        {
          method: "POST",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/x-www-form-urlencoded",
          },
          credentials: "include",
        }
      );

      const services = await serviceResponse.json();
      
      // Filter services to only include IDs 4 and 5
      const filteredServices = services.filter((s) => 
        CONFIG.SERVICE_IDS.includes(parseInt(s.id))
      );

      console.log("📦 Available services:", filteredServices);

      // Process and store available services
      const processedServices = filteredServices.map((service) => ({
        id: service.id,
        name: service.name,
        description: service.description || "",
        duration: service.duration,
        durationFormatted: formatDuration(service.duration),
        price: service.price && parseFloat(service.price) > 0
          ? parseFloat(service.price).toFixed(2)
          : null,
        active: service.active,
      }));

      setAvailableServices(processedServices);

      // Auto-select first service if only one is available
      if (processedServices.length === 1) {
        setSelectedServiceId(processedServices[0].id);
        const firstService = processedServices[0];
        setServiceInfo((prev) => ({
          ...prev,
          name: firstService.name,
          duration: firstService.durationFormatted,
          price: firstService.price,
        }));
      }

      // Load provider information (same for all services)
      const providerResponse = await fetch(
        `${CONFIG.API_BASE}/appointly/appointments_public/get_service_staff_public`,
        {
          method: "POST",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: `service_id=${CONFIG.SERVICE_IDS[0]}`,
          credentials: "include",
        }
      );

      const providerData = await providerResponse.json();

      if (
        providerData.success &&
        providerData.data &&
        providerData.data.staff
      ) {
        const provider = providerData.data.staff.find(
          (s) => s.staffid == CONFIG.PROVIDER_ID
        );
        if (provider) {
          setServiceInfo((prev) => ({
            ...prev,
            provider: `${provider.firstname} ${provider.lastname}`,
          }));
        }
      }
    } catch (error) {
      console.error("Error loading service info:", error);
      setServiceInfo((prev) => ({
        ...prev,
        name: "Consultation Service",
        provider: "Sprinter Van Team",
      }));
    }
  };

  const handleInputChange = (name, value) => {
    setAppointmentFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleServiceSelect = (serviceId) => {
    setSelectedServiceId(serviceId);
    const service = availableServices.find((s) => s.id === serviceId);
    if (service) {
      console.log("💰 Selected service:", service);
      setServiceInfo((prev) => ({
        ...prev,
        name: service.name,
        duration: service.durationFormatted,
        price: service.price,
      }));
      // Reset date/time selection when service changes
      setSelectedDate("");
      setSelectedTime("");
      setSelectedEndTime("");
      setTimeSlots([]);
      setShowTimeSlots(false);
      setShowPayment(false);
      setPaymentComplete(false);
    }
  };

  // Payment handlers
  const handlePaymentSuccess = async (paymentResult) => {
    console.log("Payment successful!", paymentResult);
    setPaymentComplete(true);
    setPaymentError(null);
    setShowPayment(false);
  };

  const handlePaymentError = (error, errorMessage) => {
    console.error("Payment failed:", error);
    setPaymentError(errorMessage);
    setPaymentComplete(false);
  };

  const handlePaymentProcessing = (isProcessing) => {
    setPaymentProcessing(isProcessing);
  };

  const handleDateChange = (dateValue) => {
    if (!dateValue) {
      setSelectedDate("");
      setShowTimeSlots(false);
      setTimeSlots([]);
      return;
    }

    // Validate date is in the future
    const selectedDateObj = new Date(dateValue);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    if (selectedDateObj >= tomorrow) {
      setSelectedDate(dateValue);
      setError("");
      loadTimeSlots(dateValue);
    } else {
      setError("Please select a future date.");
      setSelectedDate("");
    }
  };

  const loadTimeSlots = async (date) => {
    if (!timezone || !selectedServiceId) return;

    setIsLoadingSlots(true);
    setShowTimeSlots(true);
    setTimeSlots([]);
    setSelectedTime("");
    setSelectedEndTime("");
    setError("");

    try {
      const response = await fetch(
        `${CONFIG.API_BASE}/appointly/appointments_public/get_available_time_slots`,
        {
          method: "POST",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: `service_id=${selectedServiceId}&provider_id=${CONFIG.PROVIDER_ID}&date=${date}&timezone=${timezone}`,
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success && data.time_slots && data.time_slots.length > 0) {
        const availableSlots = data.time_slots.filter((slot) => slot.available);
        setTimeSlots(data.time_slots);

        if (availableSlots.length === 0) {
          setError(
            "No available time slots for this date. Please select another date."
          );
        }
      } else {
        setError(
          data.message ||
            "No available time slots for this date. Please select another date."
        );
        setTimeSlots([]);
      }
    } catch (error) {
      console.error("Error loading time slots:", error);
      setError("Failed to load available times. Please try again.");
      setTimeSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleTimeSlotSelect = (slot) => {
    if (!slot.available) return;

    setSelectedTime(slot.value);
    setSelectedEndTime(slot.end_time);
    setError("");
  };

  // Function to fetch lead ID using session ID
  const fetchLeadIdBySessionId = async () => {
    if (!sessionId) {
      console.warn("No session ID provided for lead lookup");
      return null;
    }

    try {
      // Use the new CRM Leads integration endpoint
      const response = await fetch(`${API_BASE_URL}/get-lead-id`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
        }),
      });

      const result = await response.json();

      if (result.lead_id) {
        console.log("✅ Lead ID found:", result.lead_id);
        return result.lead_id;
      } else {
        console.warn(
          "⚠️ Could not find lead ID:",
          result.message || "No lead found"
        );
        return null;
      }
    } catch (error) {
      console.error("❌ Error fetching lead ID:", error);
      return null;
    }
  };

  const handleProceedToPayment = () => {
    // Validation
    if (!selectedDate) {
      setError("Please select a date.");
      return;
    }

    if (!selectedTime) {
      setError("Please select a time slot.");
      return;
    }

    if (!formData.pickup_location || !formData.pickup_location.trim()) {
      setError("Please enter a pickup location.");
      return;
    }

    if (!formData.dropoff_location || !formData.dropoff_location.trim()) {
      setError("Please enter a dropoff location.");
      return;
    }

    // Show payment section
    setShowPayment(true);
    setError("");
  };

  const handleSubmit = useCallback(async () => {
    // Validation - only require payment if usePayment is true
    if (usePayment && !paymentComplete) {
      setError("Please complete the payment first.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // First, fetch the lead ID using session ID
      console.log("🔍 Fetching lead ID for session:", sessionId);
      const lead_id = await fetchLeadIdBySessionId();
      console.log("📋 Retrieved lead ID:", lead_id);
      
      if (!lead_id) {
        console.error("❌ No lead ID found for session:", sessionId);
        setError("Unable to link appointment with your information. Please try again or contact support.");
        return;
      }
      
      console.log("✅ Successfully linking appointment to lead ID:", lead_id);
      
      // Prepare form data exactly like the native appointly form
      const submitData = new FormData();
      submitData.append("lead_id", lead_id);
      submitData.append("service_id", selectedServiceId);
      submitData.append("staff_id", CONFIG.PROVIDER_ID);
      submitData.append("provider_id", CONFIG.PROVIDER_ID);
      submitData.append("date", selectedDate);
      submitData.append("start_hour", selectedTime);
      submitData.append("end_hour", selectedEndTime);
      submitData.append("timezone", timezone);
      submitData.append("subject", appointmentFormData.subject.trim());
      submitData.append("description", appointmentFormData.description.trim());

      // Submit to native appointly API
      const response = await fetch(
        `${CONFIG.API_BASE}/appointly/appointments_public/create_external_appointment`,
        {
          method: "POST",
          body: submitData,
          credentials: "include",
        }
      );
      const data = await response.json();

      if (data.success) {
        // Success! Mark as booked and show success state
        setIsBooked(true);
        setSuccess(
          `Appointment booked successfully for ${selectedDate} at ${selectedTime}`
        );

        // Call the completion callback after a brief delay to show success message
        setTimeout(() => {
          onAppointmentComplete({
            appointmentId: data.appointment_id || data.id,
            date: selectedDate,
            time: selectedTime,
            timezone: timezone,
            subject: appointmentFormData.subject.trim(),
            service: serviceInfo.name,
            provider: serviceInfo.provider,
            paymentComplete: usePayment ? paymentComplete : false,
          });
        }, 2000);
      } else {
        const errorMsg =
          data.message || "Failed to book appointment. Please try again.";
        setError(errorMsg);
        console.error("Appointment creation failed:", data);
      }
    } catch (error) {
      console.error("Error submitting appointment:", error);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [usePayment, paymentComplete, selectedServiceId, selectedDate, selectedTime, selectedEndTime, timezone, appointmentFormData, serviceInfo, onAppointmentComplete, fetchLeadIdBySessionId]);

  // Get min and max dates for date picker
  const getDateLimits = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);

    return {
      min: tomorrow.toISOString().split("T")[0],
      max: maxDate.toISOString().split("T")[0],
    };
  };

  const dateLimits = getDateLimits();

  const styles = `
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .nc-time-slot:hover:not(.unavailable) {
          border-color: #007bff !important;
          background: #f8f9fa !important;
          color: #000 !important;
        }

        .nc-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.3s ease;
        }

        .nc-input:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }

        .nc-btn-select-trip {
          display: block;
          width: 100%;
          margin-top: 15px;
          padding: 12px 20px;
          background: white;
          color: black;
          border: 2px solid black;
          border-radius: 50px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
        }

        .nc-btn-select-trip:hover {
          background: black;
          color: white;
        }
  `;

  return (
    <div className="nc-step active" id="nc-step-appointment">
      {/* <div className="nc-step-header">
        <div className="nc-step-icon">
          <i className="fas fa-calendar-check"></i>
        </div>
        <h2>Reserve Your Sprinter Van</h2>
        <p style={{ fontSize: "16px", marginBottom: "10px" }}>
          Book your luxury Sprinter van with a professional driver in 3 easy steps
        </p>
      </div> */}

      <div className="nc-form-fields">
        {/* Progress Steps Indicator */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "15px",
          marginBottom: "30px",
          padding: "20px",
          background: "#f8f9fa",
          border: "2px solid #e5e7eb",
          borderRadius: "12px",
          color: "#374151"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            opacity: selectedDate && selectedTime ? 1 : 0.6
          }}>
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: selectedDate && selectedTime ? "#000" : "#e5e7eb",
              color: selectedDate && selectedTime ? "white" : "#6b7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              transition: "all 0.3s ease"
            }}>
              {selectedDate && selectedTime ? "✓" : "1"}
            </div>
            <span style={{ fontWeight: "600", color: selectedDate && selectedTime ? "#000" : "#6b7280" }}>Pick Date & Time</span>
          </div>
          
          {usePayment && (
            <>
              <div style={{ width: "40px", height: "2px", background: "#e5e7eb" }}></div>
              
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <div style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: paymentComplete ? "#000" : "#e5e7eb",
                  color: paymentComplete ? "white" : "#6b7280",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  transition: "all 0.3s ease"
                }}>
                  {paymentComplete ? "✓" : "2"}
                </div>
                <span style={{ fontWeight: "600", color: paymentComplete ? "#000" : "#6b7280" }}>Complete Payment</span>
              </div>
            </>
          )}
          
          <div style={{ width: "40px", height: "2px", background: "#e5e7eb" }}></div>
          
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: isBooked ? "#000" : "#e5e7eb",
              color: isBooked ? "white" : "#6b7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              transition: "all 0.3s ease"
            }}>
              {isBooked ? "✓" : (usePayment ? "3" : "2")}
            </div>
            <span style={{ fontWeight: "600", color: isBooked ? "#000" : "#6b7280" }}>Confirm Booking</span>
          </div>
        </div>

        {/* Service Selection */}
        {availableServices.length > 1 && (
          <div style={{
            background: "white",
            border: "2px solid #e5e7eb",
            borderRadius: "12px",
            padding: "25px",
            marginBottom: "25px"
          }}>
            <h3 style={{ margin: "0 0 20px 0", fontSize: "18px", color: "#1f2937", fontWeight: "600" }}>
              <i className="fas fa-hand-pointer" style={{ marginRight: "10px", color: "#000" }}></i>
              Choose Your Sprinter Van Package
            </h3>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "15px"
            }}>
              {availableServices.map((service) => (
                <div
                  key={service.id}
                  onClick={() => handleServiceSelect(service.id)}
                  style={{
                    border: selectedServiceId === service.id ? "2px solid #000" : "2px solid #e5e7eb",
                    borderRadius: "12px",
                    padding: "20px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    background: selectedServiceId === service.id ? "#f8f9fa" : "white"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "10px" }}>
                    <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "#1f2937", flex: 1 }}>
                      {service.name}
                    </h4>
                    {service.price && (
                      <div style={{
                        color: "#000",
                        fontSize: "20px",
                        fontWeight: "700",
                        marginLeft: "10px"
                      }}>
                        ${service.price}
                      </div>
                    )}
                  </div>
                  {service.description && (
                    <p style={{ margin: "10px 0 0 0", fontSize: "14px", color: "#6b7280", lineHeight: "1.5" }}>
                      {service.description}
                    </p>
                  )}
                  <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fas fa-clock" style={{ color: "#000", fontSize: "12px" }}></i>
                    <span style={{ color: "#6b7280", fontSize: "13px" }}>{service.durationFormatted}</span>
                  </div>
                  <button 
                    className="nc-btn-select-trip"
                    style={
                      selectedServiceId === service.id 
                        ? { background: "black", color: "white" } 
                        : {}
                    }
                  >
                    {selectedServiceId === service.id ? (
                      <>
                        <i className="fas fa-check-circle" style={{ marginRight: "8px" }}></i>
                        Selected
                      </>
                    ) : (
                      "Select Your Trip"
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Service Summary with Price (shown after selection) */}
        {selectedServiceId && (
          <div
            className="nc-service-summary"
            style={{
              background: "#f8f9fa",
              border: "2px solid #e5e7eb",
              borderRadius: "12px",
              padding: "25px",
              marginBottom: "30px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
              <h5 style={{ margin: 0, color: "#1f2937", fontSize: "18px", fontWeight: "600" }}>
                <i className="fas fa-van-shuttle" style={{ marginRight: "10px", color: "#000" }}></i>
                {serviceInfo.name}
              </h5>
              {serviceInfo.price && (
                <div style={{
                  color: "#000",
                  padding: "8px 20px",
                  borderRadius: "25px",
                  fontSize: "24px",
                  fontWeight: "700"
                }}>
                  ${serviceInfo.price}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="fas fa-clock" style={{ color: "#000" }}></i>
                <span style={{ color: "#6b7280", fontSize: "14px" }}>{serviceInfo.duration}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="fas fa-user-tie" style={{ color: "#000" }}></i>
                <span style={{ color: "#6b7280", fontSize: "14px" }}>Professional Driver</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="fas fa-users" style={{ color: "#000" }}></i>
                <span style={{ color: "#6b7280", fontSize: "14px" }}>Up to 12 passengers</span>
              </div>
            </div>
          </div>
        )}
        {/* Service Summary */}
        {/* <div
          className="nc-service-summary"
          style={{
            background: "#f8f9fa",
            border: "2px solid #e9ecef",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "25px",
          }}
        >
          <h5 style={{ marginBottom: "15px", color: "#333" }}>
            <i
              className="fas fa-info-circle"
              style={{ marginRight: "8px", color: "#007bff" }}
            ></i>
            Appointment Details
          </h5>
          <div className="row">
            <div className="col-md-3">
              <strong>Service:</strong>
              <div style={{ color: "#666", fontSize: "14px" }}>
                {serviceInfo.name}
              </div>
            </div>
            <div className="col-md-3">
              <strong>Provider:</strong>
              <div style={{ color: "#666", fontSize: "14px" }}>
                {serviceInfo.provider}
              </div>
            </div>
            <div className="col-md-3">
              <strong>Duration:</strong>
              <div style={{ color: "#666", fontSize: "14px" }}>
                {serviceInfo.duration}
              </div>
            </div>
            {serviceInfo.price && (
              <div className="col-md-3">
                <strong>Price:</strong>
                <div style={{ color: "#666", fontSize: "14px" }}>
                  {serviceInfo.price}
                </div>
              </div>
            )}
          </div>
        </div> */}

        {/* Step 1: Date & Timezone Selection (only show after service selected) */}
        {selectedServiceId && (
          <div style={{
            background: "white",
            border: selectedDate && selectedTime ? "2px solid #000" : "2px solid #e5e7eb",
            borderRadius: "12px",
            padding: "25px",
            marginBottom: "25px",
            transition: "all 0.3s ease"
          }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: selectedDate && selectedTime ? "#000" : "#f3f4f6",
              color: selectedDate && selectedTime ? "white" : "#6b7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "20px",
              fontWeight: "bold",
              marginRight: "15px"
            }}>
              {selectedDate && selectedTime ? "✓" : "1"}
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "18px", color: "#1f2937" }}>Select Your Date & Time</h3>
              <p style={{ margin: "5px 0 0 0", fontSize: "14px", color: "#6b7280" }}>
                Choose when you need the Sprinter van
              </p>
            </div>
          </div>

          <div className="nc-field">
            <label style={{ fontWeight: "600", marginBottom: "10px", display: "block", color: "#374151" }}>
              <i className="fas fa-calendar" style={{ marginRight: "8px", color: "#000" }}></i>
              Pick a Date
            </label>
            <input
              type="date"
              className="nc-input"
              value={selectedDate}
              min={dateLimits.min}
              max={dateLimits.max}
              onChange={(e) => handleDateChange(e.target.value)}
              style={{
                width: "100%",
                marginBottom: "10px",
                padding: "12px",
                fontSize: "16px",
                border: "2px solid #e9ecef",
                borderRadius: "8px"
              }}
              placeholder="Select date"
            />
          </div>

          {/* Time Slots */}
          {showTimeSlots && (
            <div className="nc-field" style={{ marginTop: "20px" }}>
              <label style={{ fontWeight: "600", marginBottom: "15px", display: "block", color: "#374151" }}>
                <i className="fas fa-clock" style={{ marginRight: "8px", color: "#000" }}></i>
                Choose Your Time (Central Time)
              </label>
              {isLoadingSlots ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    background: "#f8f9fa",
                    borderRadius: "8px"
                  }}
                >
                  <div
                    className="nc-loading-spinner"
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      border: "4px solid #f3f3f3",
                      borderTop: "4px solid #667eea",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginBottom: "15px",
                    }}
                  ></div>
                  <div style={{ color: "#666", fontSize: "16px" }}>
                    Loading available times...
                  </div>
                </div>
              ) : timeSlots.length === 0 ? (
                <div style={{
                  textAlign: "center",
                  padding: "30px",
                  background: "#fff3cd",
                  border: "2px solid #ffc107",
                  borderRadius: "8px",
                  color: "#856404"
                }}>
                  <i className="fas fa-exclamation-triangle" style={{ fontSize: "24px", marginBottom: "10px" }}></i>
                  <div>No available time slots for this date. Please select another date.</div>
                </div>
              ) : (
                <div
                  className="nc-time-slots-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: "12px",
                    marginTop: "10px",
                  }}
                >
                  {timeSlots.map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`nc-time-slot ${
                        selectedTime === slot.value ? "selected" : ""
                      } ${!slot.available ? "unavailable" : ""}`}
                      onClick={() => handleTimeSlotSelect(slot)}
                      disabled={!slot.available}
                      style={{
                        padding: "10px",
                        border:
                          selectedTime === slot.value
                            ? "2px solid #000"
                            : "2px solid #e5e7eb",
                        borderRadius: "10px",
                        background:
                          selectedTime === slot.value
                            ? "#000"
                            : !slot.available
                            ? "#f8f9fa"
                            : "white",
                        color:
                          selectedTime === slot.value
                            ? "white"
                            : !slot.available
                            ? "#9ca3af"
                            : "#1f2937",
                        cursor: slot.available ? "pointer" : "not-allowed",
                        fontSize: "14px",
                        fontWeight: selectedTime === slot.value ? "600" : "500",
                        opacity: !slot.available ? 0.5 : 1,
                        transition: "all 0.2s ease"
                      }}
                    >
                      <i className="fas fa-clock" style={{ marginRight: "6px", fontSize: "12px" }}></i>
                      {convertTo12Hour(slot.value)} - {convertTo12Hour(slot.end_time)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Location Fields (Moved from Step 1) */}
        {selectedDate && selectedTime && (
          <div style={{
            background: "white",
            border: "2px solid #e9ecef",
            borderRadius: "12px",
            padding: "25px",
            marginBottom: "25px"
          }}>
            <h3 style={{ margin: "0 0 20px 0", fontSize: "18px", color: "#1f2937", fontWeight: "600" }}>
              <i className="fas fa-map-marker-alt" style={{ marginRight: "10px", color: "#000" }}></i>
              Trip Details
            </h3>
            
            <div className="nc-field">
              <label style={{ fontWeight: "600", marginBottom: "10px", display: "block", color: "#374151" }}>
                Pickup Location
              </label>
              <input
                type="text"
                className="nc-input"
                value={formData.pickup_location || ""}
                onChange={(e) => onInputChange && onInputChange("pickup_location", e.target.value)}
                placeholder="Where should we pick you up?"
                style={{ marginBottom: "15px" }}
              />
            </div>
            
            <div className="nc-field">
              <label style={{ fontWeight: "600", marginBottom: "10px", display: "block", color: "#374151" }}>
                Dropoff Location
              </label>
              <input
                type="text"
                className="nc-input"
                value={formData.dropoff_location || ""}
                onChange={(e) => onInputChange && onInputChange("dropoff_location", e.target.value)}
                placeholder="Where should we drop you off?"
              />
            </div>
          </div>
        )}

        {/* Appointment Details */}
        {/* <div className="nc-field">
          <label>📝 Meeting Purpose</label>
          <input
            type="text"
            className="nc-input"
            value={formData.subject}
            onChange={(e) => handleInputChange("subject", e.target.value)}
            placeholder="Brief description of what you'd like to discuss"
            required
          />
        </div> */}

        {/* Additional Notes */}
        {selectedDate && selectedTime && (
          <div style={{
            background: "white",
            border: "2px solid #e9ecef",
            borderRadius: "12px",
            padding: "25px",
            marginBottom: "25px"
          }}>
            <label style={{ fontWeight: "600", marginBottom: "10px", display: "block", color: "#374151" }}>
              <i className="fas fa-comment-dots" style={{ marginRight: "8px", color: "#000" }}></i>
              Additional Notes (Optional)
            </label>
            <textarea
              className="nc-input"
              value={appointmentFormData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows="4"
              placeholder="Tell us about your trip plans, special requests, or any questions you have..."
              style={{
                resize: "vertical",
                minHeight: "100px",
                width: "100%",
                padding: "15px",
                fontSize: "15px",
                border: "2px solid #e9ecef",
                borderRadius: "8px",
                fontFamily: "inherit"
              }}
            />
            <div style={{ marginTop: "10px", fontSize: "13px", color: "#666" }}>
              <i className="fas fa-lightbulb" style={{ marginRight: "6px", color: "#ffc107" }}></i>
              Example: "Airport pickup needed" or "Celebrating a birthday"
            </div>
          </div>
        )}

        {/* Step 2: Payment Section */}
        {usePayment && showPayment && serviceInfo.price && (
          <div style={{
            background: "white",
            border: paymentComplete ? "2px solid #000" : "2px solid #e5e7eb",
            borderRadius: "12px",
            padding: "25px",
            marginBottom: "25px",
            transition: "all 0.3s ease"
          }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "25px" }}>
              <div style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: paymentComplete ? "#000" : "#f3f4f6",
                color: paymentComplete ? "white" : "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                fontWeight: "bold",
                marginRight: "15px"
              }}>
                {paymentComplete ? "✓" : "2"}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", color: "#1f2937" }}>
                  {paymentComplete ? "Payment Completed" : "Secure Payment"}
                </h3>
                <p style={{ margin: "5px 0 0 0", fontSize: "14px", color: "#6b7280" }}>
                  {paymentComplete
                    ? `Successfully charged $${serviceInfo.price}`
                    : `Pay $${serviceInfo.price} to confirm your booking`}
                </p>
              </div>
            </div>

            {paymentComplete ? (
              <div
                className="nc-payment-success"
                style={{
                  background: "#f8f9fa",
                  border: "2px solid #000",
                  borderRadius: "12px",
                  padding: "30px",
                  textAlign: "center",
                }}
              >
                <div style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "#000",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "40px",
                  margin: "0 auto 20px"
                }}>
                  ✓
                </div>
                <h4 style={{ color: "#1f2937", marginBottom: "10px", fontSize: "20px", fontWeight: "600" }}>
                  Payment Successful!
                </h4>
                <p style={{ color: "#374151", margin: 0, fontSize: "16px" }}>
                  Your payment of <strong>${serviceInfo.price}</strong> has been processed securely.
                </p>
                <div style={{
                  marginTop: "20px",
                  padding: "15px",
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#374151"
                }}>
                  <i className="fas fa-shield-alt" style={{ marginRight: "8px" }}></i>
                  Your booking is protected and confirmed
                </div>
              </div>
            ) : (
              <div>
                <div style={{
                  background: "#f8f9fa",
                  padding: "15px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  border: "1px solid #e5e7eb"
                }}>
                  <i className="fas fa-lock" style={{ color: "#000", fontSize: "20px" }}></i>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", color: "#1f2937", marginBottom: "3px" }}>
                      Secure Payment Processing
                    </div>
                    <div style={{ fontSize: "13px", color: "#6b7280" }}>
                      Your payment information is encrypted and secure
                    </div>
                  </div>
                </div>
                
                <PaymentWrapper
                  sessionId={sessionId}
                  packageId="sprinter_van_rental"
                  customerInfo={{
                    name: `${customerInfo.first_name || ""} ${
                      customerInfo.last_name || ""
                    }`.trim(),
                    email: customerInfo.email,
                  }}
                  formData={formData}
                  servicePrice={parseFloat(serviceInfo.price)}
                  serviceInfo={serviceInfo}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  onPaymentProcessing={handlePaymentProcessing}
                />
              </div>
            )}

            {paymentError && (
              <div
                className="nc-payment-error-banner"
                style={{
                  background: "#f8d7da",
                  border: "2px solid #dc3545",
                  borderRadius: "8px",
                  padding: "15px",
                  marginTop: "20px",
                  color: "#721c24",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}
              >
                <i className="fas fa-exclamation-circle" style={{ fontSize: "20px" }}></i>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "600", marginBottom: "3px" }}>Payment Failed</div>
                  <div style={{ fontSize: "14px" }}>{paymentError}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contact Information */}
        {/* <div className="nc-field">
          <label>👤 Contact Information</label>
          <div className="row">
            <div className="col-md-6">
              <input
                type="text"
                className="nc-input"
                value={formData.firstname}
                onChange={(e) => handleInputChange("firstname", e.target.value)}
                placeholder="First Name"
                required
                style={{ marginBottom: "10px" }}
              />
            </div>
            <div className="col-md-6">
              <input
                type="text"
                className="nc-input"
                value={formData.lastname}
                onChange={(e) => handleInputChange("lastname", e.target.value)}
                placeholder="Last Name"
                required
                style={{ marginBottom: "10px" }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <input
                type="email"
                disabled
                className="nc-input"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Email Address"
                required
                style={{ marginBottom: "10px" }}
              />
            </div>
            <div className="col-md-6">
              <input
                type="tel"
                className="nc-input"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                placeholder="Phone Number (Optional)"
                style={{ marginBottom: "10px" }}
              />
            </div>
          </div>
        </div> */}



        {/* Error Message */}
        {error && (
          <div
            className="nc-error-banner"
            style={{
              background: "#f8d7da",
              border: "2px solid #dc3545",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "25px",
              display: "flex",
              alignItems: "center",
              gap: "15px"
            }}
          >
            <i className="fas fa-exclamation-triangle" style={{ fontSize: "30px", color: "#dc3545" }}></i>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "600", color: "#721c24", marginBottom: "5px", fontSize: "16px" }}>
                Oops! Something went wrong
              </div>
              <div style={{ color: "#721c24", fontSize: "14px" }}>{error}</div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div
            className="nc-success-banner"
            style={{
              background: "#d4edda",
              border: "2px solid #28a745",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "25px",
              display: "flex",
              alignItems: "center",
              gap: "15px"
            }}
          >
            <i className="fas fa-check-circle" style={{ fontSize: "30px", color: "#28a745" }}></i>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "600", color: "#155724", marginBottom: "5px", fontSize: "16px" }}>
                Success!
              </div>
              <div style={{ color: "#155724", fontSize: "14px" }}>{success}</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!hideButtons && (
          <div
            className="nc-appointment-actions"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "30px",
              gap: "15px",
            }}
          >
          {!showPayment && usePayment ? (
            <button
              type="button"
              className="nc-btn nc-btn-primary"
              onClick={handleProceedToPayment}
              disabled={!selectedDate || !selectedTime}
              style={{
                minWidth: "250px",
                padding: "16px 32px",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "50px",
                background: "transparent",
                border: "2px solid #000",
                color: "#000",
                cursor: selectedDate && selectedTime ? "pointer" : "not-allowed",
                opacity: selectedDate && selectedTime ? 1 : 0.3,
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                if (selectedDate && selectedTime) {
                  e.target.style.background = "#f8f9fa";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedDate && selectedTime) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              <i className="fas fa-lock" style={{ marginRight: "10px" }}></i>
              Proceed to Secure Payment
              <i className="fas fa-arrow-right" style={{ marginLeft: "10px" }}></i>
            </button>
          ) : (!usePayment && !showPayment) ? (
            <button
              type="button"
              className="nc-btn nc-btn-primary"
              onClick={handleSubmit}
              disabled={
                isBooked ||
                isSubmitting ||
                !selectedDate ||
                !selectedTime
              }
              style={{
                minWidth: "250px",
                padding: "16px 32px",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "50px",
                background: "transparent",
                border: "2px solid #000",
                color: "#000",
                cursor: selectedDate && selectedTime && !isBooked && !isSubmitting ? "pointer" : "not-allowed",
                opacity: selectedDate && selectedTime && !isBooked && !isSubmitting ? 1 : 0.3,
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                if (selectedDate && selectedTime && !isBooked && !isSubmitting) {
                  e.target.style.background = "#f8f9fa";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedDate && selectedTime && !isBooked && !isSubmitting) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <i
                    className="fas fa-spinner fa-spin"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Confirming Your Booking...
                </>
              ) : isBooked ? (
                <>
                  <i
                    className="fas fa-check-circle"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Booking Confirmed!
                </>
              ) : (
                <>
                  <i className="fas fa-calendar-check" style={{ marginRight: "10px" }}></i>
                  Book Sprinter Van
                  <i className="fas fa-arrow-right" style={{ marginLeft: "10px" }}></i>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              className="nc-btn nc-btn-primary"
              onClick={handleSubmit}
              disabled={
                isBooked ||
                isSubmitting ||
                (usePayment && !paymentComplete) ||
                !selectedDate ||
                !selectedTime
              }
              style={{
                minWidth: "250px",
                padding: "16px 32px",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "50px",
                background: "transparent",
                border: "2px solid #000",
                color: "#000",
                cursor: (!usePayment || paymentComplete) && !isBooked && !isSubmitting ? "pointer" : "not-allowed",
                opacity: (!usePayment || paymentComplete) && !isBooked && !isSubmitting ? 1 : 0.3,
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                if ((!usePayment || paymentComplete) && !isBooked && !isSubmitting) {
                  e.target.style.background = "#f8f9fa";
                }
              }}
              onMouseLeave={(e) => {
                if ((!usePayment || paymentComplete) && !isBooked && !isSubmitting) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <i
                    className="fas fa-spinner fa-spin"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Confirming Your Booking...
                </>
              ) : isBooked ? (
                <>
                  <i
                    className="fas fa-check-circle"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Booking Confirmed ✓
                </>
              ) : (
                <>
                  <i
                    className="fas fa-calendar-check"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Confirm Your Booking
                </>
              )}
            </button>
          )}
          </div>
        )}

        {/* Help Text */}
        <div style={{
          textAlign: "center",
          marginTop: "20px",
          padding: "15px",
          background: "#f8f9fa",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          fontSize: "14px",
          color: "#6b7280"
        }}>
          <i className="fas fa-question-circle" style={{ marginRight: "8px", color: "#000" }}></i>
          Need help? Contact us at <strong style={{ color: "#1f2937" }}>(615) 499-9603</strong> or <strong style={{ color: "#1f2937" }}>service@nashvilletnconcierge.com</strong>
        </div>
      </div>

      <style jsx>{styles}</style>
    </div>
  );
};

export default AppointmentBooking;
